/* eslint react/prop-types: 0 */
import 'build-and-quote/components/ShowroomHeader/showroomHeader.scss';

import PropTypes from 'prop-types';
import React from 'react';

import { LocaleService } from '@sm360/widget-commons-core-services';
import { REDIRECTIONS, AnalyticsService, TRACKING_TAGS as TAGS } from '@sm360/widget-commons-general';
import { Vehicle } from '@sm360/widget-commons-vehicle';
import PaymentCalculator from '@sm360/widget-payment-calculator';

import { MODEL_YEARS, CONFIRMATION } from 'build-and-quote/constants/sections.js';
import URL from 'build-and-quote/constants/url.js';

import ShowroomProgressBarStage from 'build-and-quote/components/ShowroomHeader/component/ShowroomProgressBarStage.jsx';

import ConfigsService from 'build-and-quote/services/ConfigsService/ConfigsService.js';
import PROGRESS_BAR_STAGES from 'build-and-quote/components/ShowroomHeader/const.js';

const ShowroomHeader = ({
    currentSection,
    imageServer,
    modelTrim,
    modelTrimExtension,
    modelYearColor,
    optionsPackage,
    hasOptionPackage,
    selectedModel,
    vehicle,
    hasAdobeTagging,
    displayCreditRate,
    displayFactoryOrder,
}) => {
    const selectedVehiclePicture = modelYearColor;
    const imageServerDomain = imageServer
        ? imageServer.imageServerDomain + imageServer.imageServerBreadcrumb
        : undefined;

    let modelTrimSubTitle = modelTrimExtension;
    let showroomHeaderBlock;
    if (modelTrimExtension && optionsPackage) {
        modelTrimSubTitle = `${modelTrimExtension} • ${optionsPackage}`;
    } else if (optionsPackage) {
        modelTrimSubTitle = optionsPackage;
    }

    const ShowroomProgressBarBlock = PROGRESS_BAR_STAGES.map((elem, index) => (
        <ShowroomProgressBarStage
            stageName={elem}
            stageId={index}
            key={elem}
            visible={
                index < 3 ||
                (index === 3 && !selectedModel.isSingleConfiguration) ||
                (index === 4 && selectedModel.hasOptions && hasOptionPackage) ||
                index > 4
            }
            currentSection={currentSection}
            canModify={index < currentSection}
            vehicleId={vehicle.vehicleId}
        />
    ));

    // TODO [landrover]: Remove Land Rover logic once we have flag from backend
    const getSpecificMake = (make, model) => {
        // As per Land rover it should display make as Land rover only for Discovery models
        // All other Land rover models (Range rover, sports, defender etc..) should not have make Land rover
        const makeName = make.toLowerCase();
        if (makeName === 'land-rover' || makeName === 'land rover') {
            const modelName = model.toLowerCase();
            // eslint-disable-next-line no-useless-escape
            const discoveryModelPattern = /^discovery[\s\-]?/i;
            const hasDiscovery = discoveryModelPattern.test(modelName);
            if (hasDiscovery) {
                // If Discovery model return make and for other return emtpy string
                return make;
            }
            return '';
        }
        return make || '';
    };

    const infoVehicle = LocaleService.t('SHOWROOMHEADER.INFOVEHICLE', {
        make: getSpecificMake(selectedModel.make, selectedModel.model),
        model: selectedModel.model,
        trim: modelTrim,
        subtrim: modelTrimSubTitle,
        year: selectedModel.year,
    });

    const locale = LocaleService.getSimpleLocale();
    const catalogUrl = REDIRECTIONS[locale].catalog;

    const { model } = selectedModel;
    const trackBackToCatalogBtn = () => AnalyticsService.gaTrigger(TAGS.SEE_THE_COLLECTION(model));
    const trackCalculatorOpening = () => AnalyticsService.gaTrigger(TAGS.ADJUST_MY_PAYMENT(model));
    const headerImageAlt =
        selectedModel.make && selectedModel.model
            ? `${selectedModel.make} ${selectedModel.model}`
            : `${LocaleService.t('SUMMARY.DEFAULT_IMAGE')}`;

    if (ConfigsService.isCatalogAvailable()) {
        showroomHeaderBlock = (
            <div className="section-showroom-header__wrapper">
                <ul className="section-showroom-header__model-listing-items">
                    <li className="section-showroom-header__model-listing-item catalog_return ">
                        <a className="back" href={catalogUrl} role="button" onClick={trackBackToCatalogBtn}>
                            <icon className="icon-review" />
                            <span className="is-util">{LocaleService.t('BACKTOSHOWROOM')}</span>
                        </a>
                    </li>
                    <li className="section-showroom-header__model-listing-item photo-vehicle">
                        <img
                            alt={headerImageAlt}
                            src={
                                imageServerDomain && selectedVehiclePicture
                                    ? imageServerDomain + selectedVehiclePicture
                                    : `${imageServer.imageServerDomain}/ir/w960h450c${URL.NO_PHOTO}`
                            }
                        />
                    </li>
                    <li className="section-showroom-header__model-listing-item info-vehicle">
                        <span dangerouslySetInnerHTML={{ __html: infoVehicle }} />
                    </li>
                    {displayFactoryOrder && (
                        <li className="section-showroom-header__model-listing-item factory-order-label">
                            <span>{LocaleService.t('VEHICLE_STATUS.FACTORY_ORDER')}</span>
                        </li>
                    )}
                </ul>
                <ul className="section-showroom-header__progressBar-stages">{ShowroomProgressBarBlock}</ul>
            </div>
        );
    }
    return (
        <section className="bloc-container section-showroom-header">
            {showroomHeaderBlock}
            {currentSection !== MODEL_YEARS.value &&
                currentSection !== CONFIRMATION.value && (
                    <PaymentCalculator.PaymentCalculator
                        onOpen={trackCalculatorOpening}
                        hasAdobeTagging={hasAdobeTagging}
                        displayCreditRate={displayCreditRate}
                    />
                )}
        </section>
    );
};

ShowroomHeader.defaultProps = {
    modelTrim: '',
    modelTrimExtension: '',
    modelYearColor: '',
    optionsPackage: '',
    selectedModel: [],
    hasOptionPackage: false,
    vehicle: new Vehicle.VehicleModelV25(),
    hasAdobeTagging: false,
    displayCreditRate: false,
    displayFactoryOrder: false,
};

ShowroomHeader.propTypes = {
    currentSection: PropTypes.number.isRequired,
    imageServer: PropTypes.shape({
        imageServerDomain: PropTypes.string.isRequired,
        imageServerFullSize: PropTypes.string.isRequired,
    }).isRequired,
    modelTrim: PropTypes.string,
    modelTrimExtension: PropTypes.string,
    modelYearColor: PropTypes.string,
    optionsPackage: PropTypes.string,
    hasOptionPackage: PropTypes.bool,
    selectedModel: PropTypes.shape({
        year: PropTypes.number.isRequired,
        make: PropTypes.string.isRequired,
        model: PropTypes.string.isRequired,
        isSingleConfiguration: PropTypes.bool.isRequired,
    }),
    vehicle: PropTypes.instanceOf(Vehicle.VehicleModelV25),
    hasAdobeTagging: PropTypes.bool,
    displayCreditRate: PropTypes.bool,
    displayFactoryOrder: PropTypes.bool,
};

ShowroomHeader.displayName = 'ShowroomHeader';

export default ShowroomHeader;
